import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, NgZone, OnDestroy, OnInit, Optional, Output, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router, RouterModule } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { UnifiedAuthService } from '../../../services/unified-auth.service';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { UserLoginResponse } from '../../../services/responses/pessoa.response';
import { DefaultColorEnum } from '../../../enum/default-color.enum';
import { AlertService, AlertType } from '../../../services/alert.service';
import { Subscription } from 'rxjs';
import { PreferencesService } from '../../../../preferences.service';
import {
  PerfilStruct,
  SubPerfilStruct,
} from '../../../services/structs/pessoa/pessoa.struct';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OneButtonComponent } from '../../one-button/one-button.component';
import { AiAgentService } from '../../../services/API/ai-agent.service';
import { montarAtalhosRapidos, rotaDoAtalho } from './acoes-rapidas.helpers';
import { RoutesService } from '../../../../routes.service';


@Component({
    selector: 'app-action-nav',
    imports: [
        CommonModule,
        RouterModule,
        MatCardModule,
        MatIconModule,
        MatTooltipModule,
        OneButtonComponent,
    ],
    templateUrl: './action-nav.component.html',
    styleUrl: './action-nav.component.css',
    animations: [
        trigger('slideFromRight', [
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('120ms ease-out', style({ transform: 'translateX(0)' })),
            ]),
            transition(':leave', [
                animate('120ms ease-in', style({ transform: 'translateX(100%)' })),
            ]),
        ]),
        trigger('slideFromBottom', [
            transition(':enter', [
                style({ transform: 'translateY(100%)' }),
                animate('120ms ease-out', style({ transform: 'translateY(0)' })),
            ]),
            transition(':leave', [
                animate('120ms ease-in', style({ transform: 'translateY(100%)' })),
            ]),
        ]),
    ]
})
export class ActionNavComponent implements OnInit, OnDestroy {
  private readonly aiAgent = inject(AiAgentService);

  @Output() showActionNavFalse = new EventEmitter<number>();
  @Output() toggleActionNav = new EventEmitter<void>();
  public color: string = DefaultColorEnum.Cor;
  public subColor: string = DefaultColorEnum.Cor;
  public listPerfil: PerfilStruct[];
  public listSubPerfil: SubPerfilStruct[];
  public modules: any[] = [];
  public accesColor: string = DefaultColorEnum.Acesso;
  public userLoginResponse: UserLoginResponse;
  public isLoading: boolean = true;
  showActionNav: boolean = false;
  private subscriptions = new Subscription();
  private _mouseMoveListener!: (event: MouseEvent) => void;
  currentRoute: string = '';
  hoveredModule: any = null;
  submenuTop = 60; // Padrão
  submenuBottom = 0; // Usado quando o painel é ancorado pela base (último módulo)
  submenuAnchorBottom = false; // Config/último módulo: encosta na borda inferior
  submenuLeft = 210; // Sidebar expandida
  isMobileMenuOpen: boolean = false;
  isPlusOpen: boolean = false;
  plusMode: 'financeiro' | 'fiscal' | 'cadastro' | '' = '';
  public isAdminContext: boolean = false;

  constructor(
    private eRef: ElementRef,
    private ngZone: NgZone,
    public authService: UnifiedAuthService,
    public alertService: AlertService,
    private router: Router,
    private navbarPreferenceService: PreferencesService,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object,
    private cdr: ChangeDetectorRef,
    @Optional() private routesService: RoutesService,
  ) {}

  openAiChat(): void {
    this.aiAgent.abrir();
  }

  private getUrlPrefix(): string {
    return this.router.url.startsWith('/admin') ? '/admin' : '';
  }

  ngOnInit(): void {
    this.isAdminContext = this.router.url.startsWith('/admin') ||
      (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.'));

    this.userLoginResponse = this.authService.getUserInfos();

    this.listSubPerfil =
      this.userLoginResponse.usuarioPermissionStruct?.listSubPerfilStruct ?? [];

    this.listPerfil =
      this.userLoginResponse.usuarioPermissionStruct?.listPerfilStruct ?? [];

    this.cardLogical();

    this.navbarPreferenceService.loadCorSistema();

    this.subscriptions.add(
      this.router.events.subscribe(() => {
        this.currentRoute = this.router.url;
        // Filho de um pai OnPush (MenuComponent): marcar a cadeia como suja para
        // que o realce do item ativo acompanhe a navegação mesmo quando a página
        // de destino fica presa em loading e nada mais dispara o change detection.
        this.cdr.markForCheck();
      })
    );

    this.currentRoute = this.router.url;

    this._mouseMoveListener = (event: MouseEvent) => {
      if (!this.eRef.nativeElement.contains(event.target) && this.hoveredModule !== null) {
        this.ngZone.run(() => { this.hoveredModule = null; this.cdr.markForCheck(); });
      }
    };
    if (isPlatformBrowser(this.platformId)) {
      this.ngZone.runOutsideAngular(() => {
        document.addEventListener('mousemove', this._mouseMoveListener);
      });
    }
  }

  isActive(module: PerfilStruct): boolean {
    if (!(module as any)?.redirect) return false;
    const prefix = this.isAdminContext ? 'admin/' : '';
    let transformation: string = this.currentRoute.substring(1) + '/';
    if (transformation.startsWith(prefix)) {
      transformation = transformation.substring(prefix.length);
    }
    return transformation.startsWith(module.redirect);
  }

  isHome(): boolean {
    return this.router.url.endsWith('/home');
  }

  goHome(): void {
    this.resilientNavigate([this.getUrlPrefix() + '/home']);
  }

  navigateTo(route: string): void {
    let finalRoute = route.startsWith('/') ? route : '/' + route;
    this.resilientNavigate([this.getUrlPrefix() + finalRoute]);
  }

  /**
   * Navegação resiliente do menu. Um menu NUNCA pode ficar "preso" porque uma
   * página está em loading infinito. Garantias:
   *  - fecha hover/overlays antes de sair (estado de menu sempre limpo);
   *  - executa dentro do NgZone mesmo que o clique chegue com a zona instável
   *    (request pendente de outra página), para o Router processar a navegação;
   *  - observa o resultado do router.navigate — em DEV/STG loga quando a rota
   *    é recusada (guard/rota) ou lança, dando evidência do ponto de falha;
   *  - markForCheck ao final (este componente vive sob um pai OnPush).
   */
  private resilientNavigate(commands: any[], extras?: NavigationExtras): void {
    this.hoveredModule = null;
    this.isMobileMenuOpen = false;
    this.isPlusOpen = false;
    this.plusMode = '';

    this.ngZone.run(() => {
      const nav = extras
        ? this.router.navigate(commands, extras)
        : this.router.navigate(commands);

      nav
        .then((ok) => {
          if (!ok && !environment.production) {
            console.warn('[action-nav] navegação não concluída (guard/rota):', commands);
          }
          this.cdr.markForCheck();
        })
        .catch((err) => {
          if (!environment.production) {
            console.error('[action-nav] erro ao navegar:', commands, err);
          }
          this.cdr.markForCheck();
        });
    });
  }

  isActiveSub(module: SubPerfilStruct): boolean {
    const prefix = this.isAdminContext ? 'admin/' : '';
    let transformation: string = this.currentRoute.substring(1) + '/';
    if (transformation.startsWith(prefix)) {
      transformation = transformation.substring(prefix.length);
    }
    return transformation.startsWith(module.redirectSubperfil);
  }

  onCardClick(card: any): void {
    // let filteredModule = this.listSubPerfil.find(
    //   (module) => module.idPerfil === card.idPerfil
    // );
    // if (filteredModule) {
    //   this.router.navigate([filteredModule.redirectSubperfil]);
    // } else {
    //   this.alertService.show(
    //     'Ops!',
    //     'Você não possui acesso a este perfil',
    //     AlertType.warning
    //   );
    // }
  }

  onSubCardClick(card: any): void {
    if (card?.isNovo) {
      // Fecha o menu antes de sair e garante a navegação dentro do NgZone,
      // mesmo com a zona instável por um request pendente de outra página.
      this.hoveredModule = null;
      this.isMobileMenuOpen = false;
      if (this.isAdminContext) {
        // Admin: a rota vem do redirectSubperfil do token; "Nova Empresa" já é o formulário (rotaDireta).
        const sp = this.listSubPerfil.find((s) => s.idSubperfil === card.idSubperfil);
        if (sp?.redirectSubperfil) this.resilientNavigate(rotaDoAtalho(sp.redirectSubperfil, !!card.rotaDireta, this.getUrlPrefix()));
        return;
      }
      this.ngZone.run(() => this.routesService?.navigateToSubperfilNovo(card.idSubperfil));
      return;
    }

    let filteredModule = this.listSubPerfil.find(
      (module) => module.idSubperfil === card.idSubperfil
    );
    if (filteredModule) {
      let target = filteredModule.redirectSubperfil;
      target = target.startsWith('/') ? target : '/' + target;
      this.resilientNavigate([this.getUrlPrefix() + target]);
    } else {
      this.hoveredModule = null;
      this.isMobileMenuOpen = false;
      this.alertService.show(
        'Ops!',
        'Você não possui acesso a este perfil',
        AlertType.warning
      );
    }
  }

  cardLogical() {
    this.modules = this.listPerfil.map((modulo) => {
      return {
        ...modulo,
        corDeFundo: '',
        submodules: this.listSubPerfil.filter(
          (item) => item.idPerfil === modulo.idPerfil
        ),
      };
    });

    const quickAdd = this.buildQuickAddModule();
    if (quickAdd) this.modules = [quickAdd, ...this.modules];
  }

  private buildQuickAddModule(): any | null {
    // Catálogo por contexto: o admin tem os próprios atalhos (+ Empresa, + Usuário app, + Usuário admin,
    // + Distribuidor); antes o sidebar do admin reaproveitava o catálogo do ERP e mostrava atalhos errados.
    // Acesso vem do sistema: precisa do subperfil E papel de escrita (gravar/total).
    const submodules = montarAtalhosRapidos(this.listSubPerfil, this.isAdminContext ? 'admin' : 'app');

    if (submodules.length === 0) return null;

    return {
      nomePerfil: 'Ações rápidas',
      googleIcone: 'add',
      isQuickAdd: true,
      submodules,
    };
  }

  clickContainerOut() {
    this.showActionNavFalse.emit();
  }

  onModuleMouseLeave() {
    this.hoveredModule = null;
  }

  onToggleNav(): void {
    this.navbarPreferenceService.toggleNavbarState();
  }

  onModuleMouseEnter(module: any, event?: MouseEvent): void {
    // Re-entradas do mesmo módulo (mouseenter ao voltar do submenu, click após hover) não
    // reprocessam posição nem re-renderizam — quebra o loop enter/leave em viewports baixas
    // (1366x768), onde o reposicionamento podia colocar o painel sob o cursor.
    if (this.hoveredModule === module) return;
    this.hoveredModule = module;

    const target = (event?.target as HTMLElement)?.closest('.menu-item');
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const submenuHeight = (module.submodules?.length + 2 || 1) * 44; // altura média dos itens
    const viewportHeight = window.innerHeight;

    const isLastModule =
      this.modules.indexOf(module) === this.modules.length - 1;

    let top: number;

    if (isLastModule) {
      // Último módulo (config) → ancora pela BASE, encostando na borda inferior.
      // Alinhar a base do painel à base do item elimina o vão que a estimativa de
      // altura deixava (o painel real quase sempre é menor que submenuHeight).
      this.submenuAnchorBottom = true;
      this.submenuBottom = Math.max(10, viewportHeight - rect.bottom);
      this.submenuLeft = rect.right + 10;
      return;
    }

    this.submenuAnchorBottom = false;
    top = rect.top;
    if (top + submenuHeight > viewportHeight) {
      top = viewportHeight - submenuHeight - 10;
      if (top < 10) top = 10;
    }

    const left = rect.right + 10;

    this.submenuTop = top;
    this.submenuLeft = left;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (isPlatformBrowser(this.platformId) && this._mouseMoveListener) {
      document.removeEventListener('mousemove', this._mouseMoveListener);
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.hoveredModule = null;
    }
  }

}
